import type { MidiEvent } from 'Models/Phrase';

/**
 * What an ungraded sight reading player plays, for the play-time batches.
 *
 * Graded sight reading sends the notes it judged. Ungraded play judges nothing,
 * so there are no judged notes to send; what the player played is the raw key
 * presses and releases recorded on each phrase, the same raw input repertoire
 * sends. Each batch carries one phrase's presses since its last batch, with no
 * judged notes.
 */

type PhraseWithRawInput = {
  uuid: string;
  takeNewMidiEvents: () => MidiEvent[];
};

type StreamWithPhrases = {
  getNBeforePhrase: (nBefore: number) => PhraseWithRawInput | undefined | null;
};

export type UngradedPlayTimeBatch = {
  phraseS3Id: string;
  notes: [];
  midiData: MidiEvent[];
};

/**
 * One batch per phrase (the current one and up to `lookback` before it) that
 * holds raw input not yet sent. Each press is taken once.
 */
export const ungradedSightReadingBatches = (
  stream: StreamWithPhrases,
  lookback: number = 3,
): UngradedPlayTimeBatch[] => {
  const phrases = Array.from({ length: lookback + 1 }, (_, nBefore) =>
    stream.getNBeforePhrase(nBefore),
  ).filter((phrase): phrase is PhraseWithRawInput => !!phrase);
  const distinct = phrases.filter(
    (phrase, index) => phrases.findIndex((other) => other.uuid === phrase.uuid) === index,
  );
  return distinct
    .map((phrase) => ({
      phraseS3Id: phrase.uuid,
      notes: [] as [],
      midiData: phrase.takeNewMidiEvents(),
    }))
    .filter((batch) => batch.midiData.length > 0);
};
